import React, { useMemo } from 'react';

import { MIN_ADULTS } from '../../constants/tripSearchParty';
import { useInputModality } from '../../hooks/useInputModality';
import { FONTS, RADII, SEARCH_LAYER } from '../../styles/tokens';
import { resolveCalendarWindow } from '../../utils/calendarWindow';
import { aWeekRange, thisWeekendRange } from '../../utils/tripDatePresets';
import { SearchTab, WHERE_FIELD_COPY, cityTokenOf } from '../../utils/tripSearchResults';
import { AVAILABILITY_CALENDAR_WINDOW_DAYS } from '../booking/bookingPolicyConstants';
import { AvailabilityRangeCalendar } from '../booking/calendar/AvailabilityRangeCalendar';
import Stepper from '../common/Stepper';
import ListingSuggestionGroup, { BellIcon } from './ListingSuggestionGroup';
import { listingCountLabel, useWhereSuggestions } from './useWhereSuggestions';

const PHONE_DESTINATION_LIMIT = 5;
const ROW_HEIGHT = 56;
const TILE_SIZE = 44;
const CHIP_HEIGHT = 44;
// Widens the calendar row past the card padding so seven 44px day cells fit at 360px.
const CALENDAR_BLEED = -12;

export const noUnavailableDates = () => false;

interface WherePanelProps {
  query: string;
  onQueryChange: (query: string) => void;
  onPickDestination: (destination: string) => void;
  onNavigateListing: () => void;
  onSubmit: () => void;
  /** The picked destination; empty until one is picked. */
  destination: string;
  onBrowseServices: () => void;
  /** Picks the wording, the suggestions' inventory and whether services are offered. */
  tab: SearchTab;
}

const PinIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    aria-hidden="true"
  >
    <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

export const WherePanel: React.FC<WherePanelProps> = ({
  query,
  onQueryChange,
  onPickDestination,
  onNavigateListing,
  onSubmit,
  destination: pickedDestination,
  onBrowseServices,
  tab,
}) => {
  const { destinations, destinationKind, listings } = useWhereSuggestions(query, {
    destinationLimit: PHONE_DESTINATION_LIMIT,
    tab,
  });
  const { placeholder, matchesHeading, suggestedListingsHeading } = WHERE_FIELD_COPY[tab];
  const { isPointerFocus, onPointerDown, onKeyDown, onBlur } = useInputModality();

  return (
    <div>
      <input
        className="form-input trip-search-where-input"
        data-surface="cream"
        type="text"
        inputMode="search"
        enterKeyHint="search"
        autoComplete="off"
        aria-label={placeholder}
        placeholder={placeholder}
        value={query}
        data-pointer-focus={isPointerFocus ? 'true' : undefined}
        onChange={(event) => onQueryChange(event.target.value)}
        onPointerDown={onPointerDown}
        onBlur={onBlur}
        onKeyDown={(event) => {
          onKeyDown(event);
          if (event.key === 'Enter') {
            // Search closes the sheet and focus returns to the bar; an unconsumed
            // Enter would then press that bar and reopen the sheet.
            event.preventDefault();
            onSubmit();
          }
        }}
        style={{ boxSizing: 'border-box' }}
      />
      {destinations.length > 0 && (
        <div className="suggestion-group-heading" data-surface="cream">
          {query.trim() ? 'Destinations' : 'Suggested destinations'}
        </div>
      )}
      {destinations.map((destination) => (
        <button
          key={destination.location}
          type="button"
          className="focus-ring"
          onClick={() => onPickDestination(destination.location)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            width: '100%',
            minHeight: ROW_HEIGHT,
            padding: '6px 4px',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            color: SEARCH_LAYER.ink,
            fontFamily: FONTS.body,
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: TILE_SIZE,
              height: TILE_SIZE,
              flexShrink: 0,
              borderRadius: RADII.iconBox,
              background: SEARCH_LAYER.card,
              border: `1px solid ${SEARCH_LAYER.stroke}`,
              boxSizing: 'border-box',
              display: 'grid',
              placeItems: 'center',
              color: SEARCH_LAYER.inkSecondary,
            }}
          >
            <PinIcon />
          </span>
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 15 }}>{destination.location}</span>
            <span style={{ fontSize: 13, color: SEARCH_LAYER.inkMuted }}>
              {listingCountLabel(destination.listing_count, destinationKind)}
            </span>
          </span>
        </button>
      ))}
      <ListingSuggestionGroup
        listings={listings}
        heading={query.trim() ? matchesHeading : (suggestedListingsHeading ?? matchesHeading)}
        onNavigate={onNavigateListing}
        surface="cream"
      />
      {/* The services sheet already lands on services in the picked place. */}
      {pickedDestination && tab !== 'services' && (
        <button
          type="button"
          className="focus-ring"
          onClick={onBrowseServices}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            width: '100%',
            minHeight: ROW_HEIGHT,
            marginTop: 8,
            padding: '6px 4px',
            background: 'none',
            border: 'none',
            borderTop: `1px solid ${SEARCH_LAYER.stroke}`,
            textAlign: 'left',
            color: SEARCH_LAYER.inkSecondary,
            fontFamily: FONTS.body,
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: TILE_SIZE,
              height: TILE_SIZE,
              flexShrink: 0,
              borderRadius: RADII.iconBox,
              background: SEARCH_LAYER.card,
              border: `1px solid ${SEARCH_LAYER.stroke}`,
              boxSizing: 'border-box',
              display: 'grid',
              placeItems: 'center',
              color: SEARCH_LAYER.inkSecondary,
            }}
          >
            <BellIcon />
          </span>
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 15 }}>Services in {cityTokenOf(pickedDestination)}</span>
            <span style={{ fontSize: 13, color: SEARCH_LAYER.inkMuted }}>
              Chefs, guides and transfers
            </span>
          </span>
        </button>
      )}
    </div>
  );
};

interface WhenPanelProps {
  checkin: string | null;
  checkout: string | null;
  onDatesChange: (checkin: string, checkout: string) => void;
}

const chipStyle: React.CSSProperties = {
  minHeight: CHIP_HEIGHT,
  padding: '0 16px',
  borderRadius: RADII.pill,
  background: 'transparent',
  border: `1px solid ${SEARCH_LAYER.stroke}`,
  color: SEARCH_LAYER.ink,
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

export const WhenPanel: React.FC<WhenPanelProps> = ({ checkin, checkout, onDatesChange }) => {
  const calendarWindow = useMemo(
    () => resolveCalendarWindow(new Date(), AVAILABILITY_CALENDAR_WINDOW_DAYS),
    [],
  );

  const applyPreset = (range: { checkin: string; checkout: string }) =>
    onDatesChange(range.checkin, range.checkout);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          type="button"
          className="focus-ring"
          style={chipStyle}
          onClick={() => applyPreset(thisWeekendRange(new Date()))}
        >
          This weekend
        </button>
        <button
          type="button"
          className="focus-ring"
          style={chipStyle}
          onClick={() => applyPreset(aWeekRange(new Date()))}
        >
          A week
        </button>
      </div>
      <div style={{ margin: `0 ${CALENDAR_BLEED}px` }}>
        <AvailabilityRangeCalendar
          aria-label="Trip dates"
          startDate={checkin ?? ''}
          endDate={checkout ?? ''}
          onRangeChange={onDatesChange}
          isIsoDateUnavailable={noUnavailableDates}
          minIso={calendarWindow.minIso}
          maxIso={calendarWindow.maxIso}
          visibleMonths={1}
          surface="cream"
        />
      </div>
    </div>
  );
};

interface WhoPanelProps {
  adults: number;
  childCount: number;
  onAdultsChange: (adults: number) => void;
  onChildrenChange: (children: number) => void;
}

export const WhoPanel: React.FC<WhoPanelProps> = ({
  adults,
  childCount,
  onAdultsChange,
  onChildrenChange,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    <Stepper
      surface="cream"
      label="Adults"
      helper="Ages 13 or above"
      value={adults}
      min={MIN_ADULTS}
      onChange={onAdultsChange}
    />
    <div style={{ height: 1, background: SEARCH_LAYER.stroke }} />
    <Stepper
      surface="cream"
      label="Children"
      helper="Ages 2 – 12"
      value={childCount}
      onChange={onChildrenChange}
    />
  </div>
);
